import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  ConfigurationGroupDeviceType,
  ValidNameRegex,
  ValidVersionRegex,
} from '@casops/store/apis/commonModels';
import {
  ConfigPackageFileType,
  useGetConfigurationGroupQuery,
} from '@casops/store/apis/configurationGroupsApi';
import { MINUS_ONE } from '@casops/shared/constants';
import { skipToken } from '@reduxjs/toolkit/query';
import { useParams } from 'react-router-dom';
import { useGetListLibraryQuery } from '@casops/store/apis/libraryApi';
import { useGetLibraryItemTypesQuery } from '@casops/store/apis/settingsApi';
import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';

const NAME_MAX = 50;
const VERSION_MAX = 50;

type useConfigurationGroupPackageProps = {
  isCustomFormType: boolean;
  isLibraryFormType: boolean;
  libraryTypeId: number;
};

const useConfigurationGroupPackage = ({
  isCustomFormType,
  isLibraryFormType,
  libraryTypeId,
}: useConfigurationGroupPackageProps) => {
  const { t } = useTranslation();
  const { configGroupId } = useParams();
  const queryParams = configGroupId ? { id: Number(configGroupId) } : skipToken;

  const { data: dataConfigurationGroup } = useGetConfigurationGroupQuery(queryParams, {
    refetchOnMountOrArgChange: 60,
  });
  const deviceType = dataConfigurationGroup?.hw;

  const { data: dataLibraryVersions } = useGetListLibraryQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });
  const libraryVersions = useMemo(() => dataLibraryVersions ?? [], [dataLibraryVersions]);

  const { data: dataLibraryTypes } = useGetLibraryItemTypesQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });
  const libraryTypes = useMemo(() => dataLibraryTypes ?? [], [dataLibraryTypes]);

  const libraryTypesOptions = useMemo(() => {
    let libraryTypesWithVersions = libraryTypes.filter(libraryType =>
      libraryVersions.some(libraryVersion => libraryVersion.ty === libraryType.id),
    );
    if (deviceType !== ConfigurationGroupDeviceType.Generic) {
      libraryTypesWithVersions = libraryTypesWithVersions.filter(libraryType =>
        libraryType.cd.includes(deviceType as unknown as AssetDeviceType),
      );
    }
    return libraryTypesWithVersions.map(libraryType => ({
      key: String(libraryType.id),
      value: libraryType.id,
      stringValue: String(libraryType.id),
      label: libraryType.n,
    }));
  }, [deviceType, libraryTypes, libraryVersions]);

  const libraryVersionOptions = useMemo(() => {
    return libraryVersions
      .filter(libraryVersion => libraryVersion.ty === libraryTypeId)
      .map(libraryVersion => ({
        key: String(libraryVersion.id),
        value: libraryVersion.id,
        stringValue: String(libraryVersion.id),
        label: libraryVersion.v,
      }));
  }, [libraryTypeId, libraryVersions]);

  const dftValuesLibraryTypes = useMemo(() => {
    const result: Record<number, string> = {};
    libraryTypes.forEach(libraryType => {
      result[libraryType.id] = libraryType.dft;
    });
    return result;
  }, [libraryTypes]);

  const configPackageFileTypeLabel = useMemo(
    () =>
      Object.entries(ConfigPackageFileType).map(([key, value]) => ({
        key,
        value,
        stringValue: value,
        label: key === 'Library' ? t('Fields.SoftwareFirmware') : t(value),
      })),
    [t],
  );

  const customFormTypeSchema = useMemo(
    () =>
      z.object({
        ver: z
          .string()
          .min(1, { message: t('Validation.SpecifyVersion') })
          .max(VERSION_MAX, { message: t('Validation.VersionTooLong', { max: VERSION_MAX }) })
          .regex(ValidVersionRegex, { message: t('Validation.InvalidCharacters') }),
      }),
    [t],
  );

  const libraryFormTypeSchema = useMemo(
    () =>
      z.object({
        softwareFirmware: z.number().gt(MINUS_ONE, { message: t('Validation.SpecifySoftware') }),
        libid: z.number().gt(MINUS_ONE, { message: t('Validation.SpecifyVersion') }),
      }),
    [t],
  );

  const baseTypeSchema = useMemo(
    () =>
      z.object({
        ty: z.nativeEnum(ConfigPackageFileType).nullable(),
        name: z
          .string()
          .min(1, { message: t('Validation.SpecifyPackageName') })
          .max(NAME_MAX, { message: t('Validation.PackageNameTooLong', { max: NAME_MAX }) })
          .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
        at: z
          .string()
          .min(1, { message: t('Validation.SpecifyDate') })
          .refine(
            val => {
              // Check if it's a valid ISO date-time string
              return !isNaN(Date.parse(val));
            },
            { message: t('Validation.SpecifyDate') },
          ),
      }),
    [t],
  );

  const schema = useMemo(() => {
    if (isCustomFormType) {
      return baseTypeSchema.merge(customFormTypeSchema);
    } else if (isLibraryFormType) {
      return baseTypeSchema.and(libraryFormTypeSchema);
    }
    return baseTypeSchema;
  }, [
    baseTypeSchema,
    customFormTypeSchema,
    isCustomFormType,
    isLibraryFormType,
    libraryFormTypeSchema,
  ]);

  return {
    schema,
    libraryTypesOptions,
    libraryVersionOptions,
    configPackageFileTypeLabel,
    dftValuesLibraryTypes,
  };
};

export default useConfigurationGroupPackage;
